/**
 * DocumentPreviewDialog Component - Story 10.3 AC4
 * Preview documents (PDF with react-pdf, images with lightbox, metadata)
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
// `lib/pdfWorker` configura o `workerSrc` (fonte única). Importar AQUI, e não no
// entry, é o que mantém o pdfjs fora do grafo ansioso: quem renderiza PDF paga.
import "@/lib/pdfWorker";
import { Document as PDFDocument, Page } from "react-pdf";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "yet-another-react-lightbox/styles.css";
import { formatBytes } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";
import { triggerBrowserDownload } from "@/lib/browser-download";
import type { Document as DocumentDetail } from "@/types/document";

// PDF.js worker é configurado UMA vez em main.tsx (same-origin, bundlado).
// Não reconfigurar aqui (evita CDN externo bloqueado por CSP + dupla config).

interface DocumentPreviewDialogProps {
  documentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DocumentPreviewDialog({
  documentId,
  open,
  onOpenChange,
}: DocumentPreviewDialogProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Fetch document details
  const { data: document, isLoading } = useQuery<DocumentDetail>({
    queryKey: ["document", documentId],
    queryFn: async () => {
      const response = await api.get(`/documents/${documentId}`);
      return response.data;
    },
    enabled: open,
  });

  // Close with ESC key (AC4 requirement)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open) {
        onOpenChange(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  // Generate preview URL when document loads
  useEffect(() => {
    if (document && open) {
      // disposition=inline: o S3 serve com Content-Disposition: inline + o
      // content-type real, então o recurso renderiza no react-pdf / <img>.
      api
        .get(`/documents/${documentId}/download`, {
          params: { disposition: "inline" },
        })
        .then((response) => {
          setPreviewUrl(response.data.download_url);
        })
        .catch((error) => {
          console.error("Failed to get preview URL:", error);
        });
      setPageNumber(1);
      setScale(1.0);
    } else {
      setPreviewUrl(null);
    }
  }, [document, open, documentId]);

  if (isLoading || !document) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl">
          {/* DialogTitle obrigatório p/ acessibilidade (screen reader); sr-only
              no estado de loading onde ainda não há nome de documento. */}
          <DialogHeader>
            <DialogTitle className="sr-only">Carregando documento</DialogTitle>
          </DialogHeader>
          <div className="py-8 text-center">Carregando...</div>
        </DialogContent>
      </Dialog>
    );
  }

  const isPDF = document.mime_type === "application/pdf";
  const isImage = document.mime_type.startsWith("image/");

  const handleDownload = async () => {
    try {
      // attachment → baixa o arquivo (nome original) sem navegar pro S3.
      const response = await api.get(`/documents/${documentId}/download`, {
        params: { disposition: "attachment" },
      });
      triggerBrowserDownload(response.data.download_url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 2.0));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="truncate">{document.name}</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleDownload}>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Preview Area - Story 10.3 AC4 */}
            <div className="border rounded-lg p-4 bg-gray-50 min-h-[400px] flex items-center justify-center">
              {isPDF && previewUrl ? (
                <div className="w-full">
                  {/* PDF Controls */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToPrevPage}
                        disabled={pageNumber <= 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm">
                        Página {pageNumber} de {numPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToNextPage}
                        disabled={pageNumber >= numPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={zoomOut} disabled={scale <= 0.5}>
                        <ZoomOut className="h-4 w-4" />
                      </Button>
                      <span className="text-sm">{Math.round(scale * 100)}%</span>
                      <Button variant="outline" size="sm" onClick={zoomIn} disabled={scale >= 2.0}>
                        <ZoomIn className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {/* PDF Viewer with react-pdf */}
                  <div className="flex justify-center">
                    <PDFDocument
                      file={previewUrl}
                      onLoadSuccess={onDocumentLoadSuccess}
                      loading={<div className="text-center py-8">Carregando PDF...</div>}
                      error={
                        <div className="text-center py-8 text-red-500">Erro ao carregar PDF</div>
                      }
                    >
                      <Page
                        pageNumber={pageNumber}
                        scale={scale}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                      />
                    </PDFDocument>
                  </div>
                </div>
              ) : isImage && previewUrl ? (
                <div className="w-full text-center">
                  <img
                    src={previewUrl}
                    alt={document.name}
                    className="max-w-full max-h-[500px] mx-auto cursor-pointer rounded"
                    onClick={() => setLightboxOpen(true)}
                  />
                  <p className="text-sm text-gray-500 mt-2">Clique para ampliar</p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-6xl mb-4">📄</div>
                  <p className="text-gray-600 mb-2">
                    Preview não disponível para este tipo de arquivo
                  </p>
                  <Badge>{document.mime_type}</Badge>
                  <Button className="mt-4" onClick={handleDownload}>
                    <Download className="h-4 w-4 mr-2" />
                    Baixar Arquivo
                  </Button>
                </div>
              )}
            </div>

            {/* Metadata Panel - Story 10.3 AC4 */}
            <div className="border rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-sm text-gray-700">Informações do Documento</h3>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Nome do arquivo:</span>
                  <p className="font-medium truncate">{document.original_filename}</p>
                </div>

                <div>
                  <span className="text-gray-500">Tamanho:</span>
                  <p className="font-medium">{formatBytes(document.file_size)}</p>
                </div>

                <div>
                  <span className="text-gray-500">Tipo:</span>
                  <p className="font-medium">{document.mime_type}</p>
                </div>

                <div>
                  <span className="text-gray-500">Enviado por:</span>
                  <p className="font-medium">{document.uploader_name || "Desconhecido"}</p>
                </div>

                <div className="col-span-2">
                  <span className="text-gray-500">Data de envio:</span>
                  <p className="font-medium">
                    {format(new Date(document.created_at), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", {
                      locale: ptBR,
                    })}
                  </p>
                </div>

                {document.description && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Descrição:</span>
                    <p className="font-medium">{document.description}</p>
                  </div>
                )}

                {document.tags && document.tags.length > 0 && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Tags:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {document.tags.map((tag) => (
                        <Badge key={tag} variant="outline">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox with zoom - Story 10.3 AC4 */}
      {isImage && previewUrl && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={[{ src: previewUrl }]}
          plugins={[Zoom]}
          zoom={{
            maxZoomPixelRatio: 3,
            scrollToZoom: true,
          }}
        />
      )}
    </>
  );
}
